<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { useOnline } from '@vueuse/core';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosUiSkeleton from '@/components/pos-v2/ui/PosUiSkeleton.vue';
  import { PLACES_SEARCH_MIN_CHARS } from '@/composition/store-setup/use-places-search';
  import type { IUsePlacesSearch } from '@/composition/store-setup/use-places-search';
  import { useUi } from '@/composition/use-ui';
  import { groupSuggestions } from '@/domain/store-setup/location-step';
  import { AlertType } from '@/interfaces/IAlert';
  import type {
    IPlaceDetails,
    IPlaceSuggestion,
  } from '@/interfaces/IStoreSetup';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * One search box for a business or an address (plan §4.1): Google results
   * through the API proxy, grouped «Negocios» / «Direcciones», keyboard
   * navigable (combobox + listbox). The input is never unmounted; the list is
   * shown with `v-show`.
   */
  const props = withDefaults(
    defineProps<{
      /** The selected country (the search composable re-scopes itself on a change). */
      country: string;
      disabled?: boolean;
      places: IUsePlacesSearch;
    }>(),
    { disabled: false }
  );
  const emit = defineEmits<{
    (e: 'pick', details: IPlaceDetails): void;
    (e: 'manual'): void;
    (e: 'focus', active: boolean): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  const ui = useUi();
  const online = useOnline();

  const uid = nextDomId('setup-place-search');
  const listId = `${uid}-list`;
  const root = ref<HTMLElement | null>(null);
  const activeIndex = ref(-1);
  /** The place id whose details are loading. */
  const picking = ref<string | null>(null);
  /** Typed text not answered yet (debounce + request): no «No encontramos» meanwhile. */
  const awaiting = ref(false);
  let alive = true;

  const query = computed(() => props.places.query.value);
  const loading = computed(() => props.places.loading.value);
  const groups = computed(() => groupSuggestions(props.places.results.value));
  /** Rows in display order (businesses first): what the arrow keys walk. */
  const rows = computed<IPlaceSuggestion[]>(() => [
    ...groups.value.businesses,
    ...groups.value.addresses,
  ]);
  const listOpen = computed(
    () => query.value.trim().length >= PLACES_SEARCH_MIN_CHARS
  );
  const activeId = computed(() =>
    listOpen.value &&
    activeIndex.value >= 0 &&
    activeIndex.value < rows.value.length
      ? optionId(activeIndex.value)
      : undefined
  );

  function optionId(index: number): string {
    return `${uid}-option-${index}`;
  }

  function addressIndex(index: number): number {
    return groups.value.businesses.length + index;
  }

  function onInput(value: string) {
    props.places.query.value = value;
    activeIndex.value = -1;
    // Offline every request would fail and count towards the manual fallback.
    if (!online.value) return;
    awaiting.value = value.trim().length >= PLACES_SEARCH_MIN_CHARS;
    props.places.search();
  }

  // Back online: the text typed meanwhile is searched.
  watch(online, (isOnline) => {
    if (isOnline && listOpen.value) props.places.search();
  });
  watch(rows, () => {
    activeIndex.value = -1;
  });
  watch(loading, (isLoading) => {
    if (!isLoading) awaiting.value = false;
  });
  watch(
    () => props.country,
    () => {
      activeIndex.value = -1;
    }
  );

  async function revealActive() {
    await nextTick();
    const id = activeId.value;
    if (!id || !root.value) return;
    const row = root.value.querySelector(`#${id}`);
    if (row instanceof HTMLElement) row.scrollIntoView({ block: 'nearest' });
  }

  function move(step: number) {
    const count = rows.value.length;
    if (!count) return;
    const start =
      activeIndex.value < 0 ? (step > 0 ? -1 : count) : activeIndex.value;
    activeIndex.value = Math.min(count - 1, Math.max(0, start + step));
    void revealActive();
  }

  function onKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      move(-1);
    } else if (event.key === 'Enter') {
      const row = rows.value[activeIndex.value];
      if (!row) return;
      event.preventDefault();
      void select(row);
    } else if (event.key === 'Escape') {
      activeIndex.value = -1;
    }
  }

  async function select(suggestion: IPlaceSuggestion) {
    if (picking.value !== null || props.disabled) return;
    picking.value = suggestion.placeId;
    try {
      const details = await props.places.pick(suggestion);
      if (!alive) return;
      emit('pick', details);
    } catch {
      if (!alive) return;
      // Two unavailable answers in a row switch the step to the manual address (it watches `failures`).
      if (props.places.failures.value < 2)
        ui.showToast(AlertType.Error, tr('storeSetup.errors.generic'));
    } finally {
      if (alive) picking.value = null;
    }
  }

  function onFocus() {
    emit('focus', true);
  }

  function onBlur() {
    emit('focus', false);
  }

  onBeforeUnmount(() => {
    alive = false;
  });
</script>

<template>
  <div ref="root" class="v2-setup-place-search">
    <PosUiInput
      class="search-input"
      :value="query"
      icon="magnify"
      size="lg"
      clearable
      autocomplete="off"
      :maxlength="120"
      :disabled="disabled"
      :placeholder="$t('storeSetup.location.searchPlaceholder')"
      :aria-label="$t('storeSetup.location.searchLabel')"
      role="combobox"
      aria-autocomplete="list"
      :aria-controls="listId"
      :aria-expanded="listOpen"
      :aria-activedescendant="activeId"
      @input="onInput"
      @keydown="onKeydown"
      @focus="onFocus"
      @blur="onBlur"
    />
    <div v-show="listOpen" class="search-results">
      <div
        :id="listId"
        class="results-list"
        role="listbox"
        :aria-label="$t('storeSetup.location.searchLabel')"
      >
        <div
          v-if="groups.businesses.length"
          class="results-group"
          role="group"
          :aria-labelledby="`${uid}-businesses`"
        >
          <p :id="`${uid}-businesses`" class="group-title" role="presentation">
            {{ $t('storeSetup.location.groupBusinesses') }}
          </p>
          <div
            v-for="(row, index) in groups.businesses"
            :id="optionId(index)"
            :key="row.placeId"
            class="result-row"
            :class="{ 'is-active': index === activeIndex }"
            role="option"
            :aria-selected="String(index === activeIndex)"
            @mousedown.prevent
            @click="select(row)"
          >
            <span class="row-icon" aria-hidden="true">
              <span v-if="picking === row.placeId" class="row-spinner" />
              <PosUiIcon v-else name="storefront-outline" />
            </span>
            <span class="row-text">
              <span class="row-main">{{ row.primaryText }}</span>
              <span v-if="row.secondaryText" class="row-sub">{{
                row.secondaryText
              }}</span>
            </span>
          </div>
        </div>
        <div
          v-if="groups.addresses.length"
          class="results-group"
          role="group"
          :aria-labelledby="`${uid}-addresses`"
        >
          <p :id="`${uid}-addresses`" class="group-title" role="presentation">
            {{ $t('storeSetup.location.groupAddresses') }}
          </p>
          <div
            v-for="(row, index) in groups.addresses"
            :id="optionId(addressIndex(index))"
            :key="row.placeId"
            class="result-row"
            :class="{ 'is-active': addressIndex(index) === activeIndex }"
            role="option"
            :aria-selected="String(addressIndex(index) === activeIndex)"
            @mousedown.prevent
            @click="select(row)"
          >
            <span class="row-icon" aria-hidden="true">
              <span v-if="picking === row.placeId" class="row-spinner" />
              <PosUiIcon v-else name="map-marker-outline" />
            </span>
            <span class="row-text">
              <span class="row-main">{{ row.primaryText }}</span>
              <span v-if="row.secondaryText" class="row-sub">{{
                row.secondaryText
              }}</span>
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="(loading || awaiting) && !rows.length"
        class="results-loading"
        aria-busy="true"
      >
        <PosUiSkeleton v-for="index in 2" :key="index" class="loading-row" />
      </div>
      <p v-else-if="!rows.length" class="results-empty" role="status">
        {{ $t('storeSetup.location.noResults') }}
      </p>
    </div>
    <button
      type="button"
      class="not-found-link"
      :disabled="disabled"
      @click="emit('manual')"
    >
      {{ $t('storeSetup.location.notFound') }}
    </button>
  </div>
</template>

<style scoped lang="scss">
  .v2-setup-place-search {
    @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-2;
    // Touch first: the clear button is a whole 44 px target (the field is 48 px tall).
    .search-input :deep(.pos-ui-input-clear) {
      @apply tw-w-11 tw-shrink-0 tw-justify-center tw-pr-0;
    }
    .search-results {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .results-list {
      @apply tw-flex tw-flex-col tw-gap-2;
    }
    .results-group {
      @apply tw-flex tw-flex-col tw-gap-0.5;
    }
    .group-title {
      @apply tw-m-0 tw-mt-1 tw-px-2 tw-text-[11px] tw-font-medium tw-uppercase tw-tracking-wider tw-text-neutral-500;
    }
    .result-row {
      @apply tw-flex tw-min-h-11 tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-lg tw-px-2 tw-py-2 tw-text-left hover:tw-bg-neutral-100;
      &.is-active {
        @apply tw-bg-neutral-100;
      }
    }
    .row-icon {
      @apply tw-flex tw-h-8 tw-w-8 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-100 tw-text-lg tw-text-neutral-600;
    }
    .result-row:hover .row-icon,
    .result-row.is-active .row-icon {
      @apply tw-bg-white;
    }
    .row-spinner {
      @apply tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-solid tw-border-neutral-400 tw-border-t-transparent;
    }
    .row-text {
      @apply tw-flex tw-min-w-0 tw-flex-col;
    }
    .row-main {
      @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .row-sub {
      @apply tw-truncate tw-text-xs tw-text-neutral-500;
    }
    .results-loading {
      @apply tw-flex tw-flex-col tw-gap-2 tw-px-2;
      .loading-row {
        @apply tw-h-11 tw-w-full;
      }
    }
    .results-empty {
      @apply tw-m-0 tw-px-2 tw-py-2 tw-text-sm tw-text-neutral-500;
    }
    .not-found-link {
      @apply tw-min-h-11 tw-self-start tw-border-0 tw-bg-transparent tw-px-0.5 tw-text-sm tw-font-medium tw-text-neutral-700 tw-underline tw-underline-offset-4 hover:tw-text-neutral-900;
      &:disabled {
        @apply tw-cursor-not-allowed tw-text-neutral-400 tw-no-underline;
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
